/**
 * Erweiterte Conversions für Google Ads: E-Mail und Telefonnummer der
 * Anfrage mitgeben, damit Google den Abschluss einem Klick zuordnen kann,
 * auch wenn das Cookie fehlt.
 *
 * BEWUSSTE ABWEICHUNG: Es gehen NUR SHA-256-Hashes in den dataLayer,
 * niemals Klartext.
 *
 * Grund: Alles, was im dataLayer steht, kann jedes Tag im Google Tag
 * Manager lesen – auch eines, das jemand später hinzufügt. Klartext-E-Mail
 * und Telefonnummer lägen damit offen im Browser und würden an jeden
 * eingebundenen Dienst weitergereicht. Google unterstützt für erweiterte
 * Conversions ausdrücklich vorgehashte Werte (`sha256_email_address`,
 * `sha256_phone_number`); die Zuordnung funktioniert damit genauso gut.
 *
 * In lib/tracking.ts steht seit jeher, dass personenbezogene Angaben nie
 * mitgegeben werden. Mit Hashes bleibt dieser Grundsatz erhalten.
 *
 * WICHTIG für die Einrichtung im Tag Manager: Im Tag „Google Ads
 * Conversion Tracking" müssen die Felder für erweiterte Conversions auf
 * `user_data.sha256_email_address` und `user_data.sha256_phone_number`
 * zeigen – nicht auf die Klartextfelder.
 */

/** Googles Normalisierung: Kleinbuchstaben, keine Leerzeichen. */
function normalisiereEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Telefonnummer nach E.164: nur Ziffern mit führendem Plus.
 *
 * Österreichische Eingaben kommen fast immer als „0660 …" – ohne
 * Länderkennung kann Google sie nicht zuordnen, deshalb wird die führende
 * Null durch +43 ersetzt. Nummern, die schon international geschrieben
 * sind, bleiben unverändert.
 */
export function normalisiereTelefon(roh: string, land = "43"): string | null {
  const nur = roh.replace(/[^\d+]/g, "");
  if (!nur) return null;
  if (nur.startsWith("+")) return nur.length >= 8 ? nur : null;
  if (nur.startsWith("00")) return "+" + nur.slice(2);
  if (nur.startsWith("0")) return `+${land}${nur.slice(1)}`;
  // Ohne führende Null und ohne Plus: nicht eindeutig, lieber weglassen
  // als eine falsche Nummer an Google zu melden.
  return null;
}

async function sha256(text: string): Promise<string | null> {
  if (typeof window === "undefined" || !window.crypto?.subtle) return null;
  try {
    const daten = new TextEncoder().encode(text);
    const puffer = await window.crypto.subtle.digest("SHA-256", daten);
    return Array.from(new Uint8Array(puffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    // crypto.subtle gibt es nur in sicheren Kontexten (HTTPS). Fehlt es,
    // wird nichts mitgegeben – lieber keine erweiterte Conversion als
    // ungehashte Daten.
    return null;
  }
}

export type UserData = {
  sha256_email_address?: string;
  sha256_phone_number?: string;
};

/**
 * Erzeugt die gehashten Angaben. Gibt ein leeres Objekt zurück, wenn
 * nichts Brauchbares übrig bleibt – der Aufrufer gibt dann kein
 * `user_data` mit, statt ein leeres Feld zu senden.
 */
export async function userDataGehasht(email: string, telefon?: string): Promise<UserData> {
  const u: UserData = {};
  const e = normalisiereEmail(email);
  if (e.includes("@")) {
    const h = await sha256(e);
    if (h) u.sha256_email_address = h;
  }
  if (telefon) {
    const t = normalisiereTelefon(telefon);
    if (t) {
      const h = await sha256(t);
      if (h) u.sha256_phone_number = h;
    }
  }
  return u;
}
